/**
 * A draft's per-language and per-sampradaya amounts (12-content/08 §1.2, CSR-143 ask 2).
 *
 * The same shape as the per-city section on `Pricing.tsx`, and staged the same way: nothing is
 * sent until Save, because each row is a price somebody will be charged. A row names a language,
 * a sampradaya or both. The server holds the precedence: both beats one, and any scope row beats
 * a city override. This screen says so and does not re-implement it.
 */
import { useState } from 'react';
import { Note } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { EmptyState } from '../components/feedback';
import { ConfirmAction, Money, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useAxisValues, type TaxonomyValue } from '../lib/api/hooks/catalogue';
import {
  useReplaceScopeOverrides,
  type PriceVersionDetail,
  type ScopeOverrideIn,
} from '../lib/api/hooks/pricing';

/** The select's "no preference" entry: the row then leaves that axis open. */
export const ANY = 'Any';

type Scoped = Pick<ScopeOverrideIn, 'ritual_language_id' | 'sampradaya_id'>;

/** "Maithili · Smarta", "Maithili", "Smarta". An id the list does not hold reads as `#id`. */
export function scopeLabel(
  o: Scoped,
  languages: readonly TaxonomyValue[],
  sampradayas: readonly TaxonomyValue[],
): string {
  const name = (list: readonly TaxonomyValue[], id: number) =>
    list.find((v) => v.id === id)?.name_en ?? `#${id}`;
  return [
    o.ritual_language_id ? name(languages, o.ritual_language_id) : null,
    o.sampradaya_id ? name(sampradayas, o.sampradaya_id) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function ScopeOverrides({ sheet }: { sheet: PriceVersionDetail }) {
  const { scope, toast } = useConsole();
  const languages = useAxisValues('ritual-languages').data?.items ?? [];
  const sampradayas = useAxisValues('sampradayas').data?.items ?? [];
  const replace = useReplaceScopeOverrides();
  const [draft, setDraft] = useState<ScopeOverrideIn[] | null>(null);
  const [language, setLanguage] = useState(ANY);
  const [sampradaya, setSampradaya] = useState(ANY);
  const [item, setItem] = useState('');
  const [paise, setPaise] = useState('');
  const [confirming, setConfirming] = useState(false);

  const rows: ScopeOverrideIn[] =
    draft ??
    (sheet.scope_overrides ?? []).map((o) => ({
      ritual_language_id: o.ritual_language_id,
      sampradaya_id: o.sampradaya_id,
      item_type: o.item_type,
      amount_paise: o.amount_paise,
      note: o.note,
    }));
  const pick = (list: readonly TaxonomyValue[], label: string) =>
    list.find((v) => v.name_en === label)?.id ?? null;
  const addable: ScopeOverrideIn | null = (() => {
    const row = {
      ritual_language_id: pick(languages, language),
      sampradaya_id: pick(sampradayas, sampradaya),
    };
    if (row.ritual_language_id === null && row.sampradaya_id === null) return null;
    if (!item || !/^\d+$/.test(paise.trim())) return null;
    return { ...row, item_type: item, amount_paise: Number(paise.trim()), note: null };
  })();
  const same = (a: ScopeOverrideIn, b: ScopeOverrideIn) =>
    a.ritual_language_id === b.ritual_language_id &&
    a.sampradaya_id === b.sampradaya_id &&
    a.item_type === b.item_type;

  return (
    <Section
      title="Per-language and sampradaya amounts"
      note="Both named beats one named; either beats a city override"
    >
      <Note>
        A row changes what a devotee pays for one line item when they ask for that ritual language
        or sampradaya. The quote shows it before they choose, so it is never a surprise at
        checkout.
      </Note>
      {rows.length === 0 && (
        <EmptyState
          icon="book-open"
          title="No language or sampradaya amounts"
          body="Every language and sampradaya is charged the version's own amounts."
          inline
        />
      )}
      {rows.map((o, index) => (
        <div
          key={`${o.ritual_language_id}-${o.sampradaya_id}-${o.item_type}`}
          style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'center' }}
        >
          <span style={{ flex: 1 }}>
            {scopeLabel(o, languages, sampradayas)} · {o.item_type}
          </span>
          <span className="mono-num tabular">{o.amount_paise}</span>
          <Money paise={o.amount_paise} showPaise />
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            variant="ghost"
            onClick={() => setDraft(rows.filter((_, i) => i !== index))}
          >
            Remove
          </ScopeAction>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', marginTop: 'var(--sp-md)' }}>
        <Select
          density="admin"
          label="Ritual language"
          options={[ANY, ...languages.map((v) => v.name_en)]}
          value={language}
          onChange={setLanguage}
          style={{ flex: 1 }}
        />
        <Select
          density="admin"
          label="Sampradaya"
          options={[ANY, ...sampradayas.map((v) => v.name_en)]}
          value={sampradaya}
          onChange={setSampradaya}
          style={{ flex: 1 }}
        />
        <Select
          density="admin"
          label="Scoped line item"
          options={sheet.line_items.map((i) => i.item_type)}
          value={item}
          onChange={setItem}
          style={{ flex: 1 }}
        />
        <Input
          density="admin"
          label="Scoped paise"
          inputMode="numeric"
          value={paise}
          onChange={setPaise}
          placeholder="260000"
        />
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          variant="secondary"
          disabled={!addable}
          onClick={() => {
            setDraft([...rows.filter((o) => !same(o, addable!)), addable!]);
            setPaise('');
          }}
        >
          Add scoped amount
        </ScopeAction>
      </div>
      <div style={{ marginTop: 'var(--sp-md)' }}>
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          disabled={draft === null}
          onClick={() => setConfirming(true)}
        >
          Save language and sampradaya amounts
        </ScopeAction>
      </div>
      <ConfirmAction
        open={confirming}
        title={`Save ${rows.length} language and sampradaya amount${rows.length === 1 ? '' : 's'}?`}
        phrase="SAVE"
        tone="warn"
        consequences={[
          `Only draft v${sheet.version_number} changes; nobody is charged these until it is published.`,
          'A devotee who asks for that language or sampradaya is quoted the scoped amount before they choose.',
        ]}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          replace.mutate(
            { versionId: sheet.id, items: rows },
            {
              onSuccess: () => {
                setDraft(null);
                toast({ tone: 'success', message: 'Language and sampradaya amounts saved' });
              },
              onError: (e: unknown) => toast({ tone: 'error', message: errorMessage(e) }),
            },
          );
        }}
        confirmLabel="Save amounts"
      />
    </Section>
  );
}
